import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Save, IndianRupee, Tag, Wallet, RefreshCw, Building2 } from 'lucide-react';
import Notification from '../../Component/Notification';

const CreateInvoice: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'new' | 'update'>('new');
    
    // Data Sources
    const [sentQuotes, setSentQuotes] = useState<any[]>([]);
    const [existingInvoices, setExistingInvoices] = useState<any[]>([]);
    
    // Shared States
    const [selectedId, setSelectedId] = useState<string>("");
    const [selectedData, setSelectedData] = useState<any | null>(null);
    const [includeBankDetails, setIncludeBankDetails] = useState<boolean>(true);
    const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
    
    // 'New Invoice' States
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [discountAmount, setDiscountAmount] = useState<number>(0);
    const [amountReceived, setAmountReceived] = useState<number>(0);

    // 'Update Invoice' States
    const [newPaymentAmount, setNewPaymentAmount] = useState<number>(0);

    // UI States
    const [isGenerating, setIsGenerating] = useState(false);
    const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' as 'success' | 'error' });

    // Fetch Data
    useEffect(() => {
        fetch('https://www.projectinfotech.in/api/quotation/history').then(res => res.json()).then(data => setSentQuotes(data)).catch(console.error);
        fetch('https://www.projectinfotech.in/api/invoice/history').then(res => res.json()).then(data => setExistingInvoices(data)).catch(console.error);
        // fetch('http://localhost:2025/api/quotation/history').then(res => res.json()).then(data => setSentQuotes(data)).catch(console.error);
        // fetch('http://localhost:2025/api/invoice/history').then(res => res.json()).then(data => setExistingInvoices(data)).catch(console.error);
    }, []);

    // Handle Tab Switch
    const handleTabChange = (tab: 'new' | 'update') => {
        setActiveTab(tab);
        setSelectedId("");
        setSelectedData(null);
        setIncludeBankDetails(tab === 'new'); // Default true for new, false/true depending on user for update
    };

    // Handle Selection
    const handleSelect = (id: string) => {
        setSelectedId(id);
        if (activeTab === 'new') {
            const quote = sentQuotes.find(q => q.id === Number(id));
            if (quote) {
                setSelectedData(quote);
                setTotalAmount(quote.totalAmount || 0);
                setDiscountAmount(0); setAmountReceived(0);
            }
        } else {
            const inv = existingInvoices.find(i => i.id === Number(id));
            if (inv) {
                setSelectedData(inv);
                setNewPaymentAmount(inv.remainingAmount || 0); // Default to remaining amount
            }
        }
    };

    // Submit Handler
    const handleGenerate = async () => {
        if (!selectedData) return;
        setIsGenerating(true);

        const url = activeTab === 'new' 
            ? 'https://www.projectinfotech.in/api/invoice/generate' 
            : 'https://www.projectinfotech.in/api/invoice/update';
            // ? 'http://localhost:2025/api/invoice/generate' 
            // : 'http://localhost:2025/api/invoice/update';

        const payload = activeTab === 'new' ? {
            quotationId: selectedData.id, clientName: selectedData.name, clientEmail: selectedData.email,
            projectTitle: selectedData.projectBrief || 'Project Development',
            totalAmount, discountAmount, amountReceived, paymentDate, includeBankDetails
        } : {
            invoiceId: selectedData.id, newPaymentAmount, paymentDate, includeBankDetails
        };

        try {
            const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (res.ok) {
                setNotify({ isOpen: true, message: `Invoice ${activeTab === 'new' ? 'Generated' : 'Updated'} Successfully!`, type: 'success' });
                setTimeout(() => navigate('/admin/invoice/history'), 2000);
            } else {
                setNotify({ isOpen: true, message: 'Failed to process request.', type: 'error' });
            }
        } catch (error) {
            setNotify({ isOpen: true, message: 'Server Error.', type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    // Calcs for UI display
    const netAmount = totalAmount - (discountAmount || 0);
    const remainingAmount = netAmount - (amountReceived || 0);

    return (
        <div className="p-6 bg-gray-50 min-h-screen animate-fade-in-up">
            <Notification isOpen={notify.isOpen} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, isOpen: false })} />
            
            <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <FileText className="text-red-600"/> Manage Invoices
            </h1>

            {/* TABS */}
            <div className="flex gap-4 mb-6 max-w-4xl">
                <button onClick={() => handleTabChange('new')} className={`flex-1 py-3 rounded-xl font-bold transition-all shadow-sm flex justify-center items-center gap-2 ${activeTab === 'new' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}>
                    <FileText size={18}/> Create New Invoice
                </button>
                <button onClick={() => handleTabChange('update')} className={`flex-1 py-3 rounded-xl font-bold transition-all shadow-sm flex justify-center items-center gap-2 ${activeTab === 'update' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}>
                    <RefreshCw size={18}/> Update Existing Payment
                </button>
            </div>
            
            {/* SELECTION DROPDOWN */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6 max-w-4xl">
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                    {activeTab === 'new' ? 'Select Sent Quotation' : 'Select Pending Invoice'}
                </label>
                <select value={selectedId} onChange={(e) => handleSelect(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-gray-50 text-gray-800 font-medium">
                    <option value="">-- Choose a Client --</option>
                    {activeTab === 'new' 
                        ? sentQuotes.map(q => <option key={q.id} value={q.id}>{q.name} - ₹{q.totalAmount}</option>)
                        : existingInvoices.filter(i => i.remainingAmount > 0).map(i => <option key={i.id} value={i.id}>{i.clientName} (Due: ₹{i.remainingAmount})</option>)
                    }
                </select>
            </div>

            {selectedData && (
                <div className={`bg-white p-8 rounded-xl shadow-lg border-t-4 max-w-4xl space-y-8 animate-fade-in ${activeTab === 'new' ? 'border-red-600' : 'border-blue-600'}`}>
                    
                    {/* TOGGLE BANK DETAILS (Shared) */}
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border">
                        <input type="checkbox" id="bankToggle" checked={includeBankDetails} onChange={e => setIncludeBankDetails(e.target.checked)} className="w-5 h-5 accent-gray-800 cursor-pointer" />
                        <label htmlFor="bankToggle" className="font-bold text-gray-700 cursor-pointer select-none flex items-center gap-2"><Building2 size={18}/> Print Bank Details on PDF</label>
                    </div>

                    {/* --- TAB 1: NEW INVOICE CONTENT --- */}
                    {activeTab === 'new' && (
                        <>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-bold text-gray-600 uppercase mb-2 block">Total Project Amount</label>
                                    <input type="number" value={totalAmount} onChange={e => setTotalAmount(Number(e.target.value))} className="w-full p-3 border rounded-lg font-bold text-lg text-gray-800 outline-none" />
                                </div>
                                <div>
                                    <label className="text-sm font-bold text-gray-600 uppercase mb-2 block">Discount (If Any)</label>
                                    <input type="number" value={discountAmount} onChange={e => setDiscountAmount(Number(e.target.value))} className="w-full p-3 border rounded-lg font-bold text-lg text-red-500 outline-none" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-6 bg-green-50 p-6 rounded-xl border border-green-200">
                                <div>
                                    <label className="text-sm font-bold text-gray-700 uppercase mb-2 block">Amount Received So Far</label>
                                    <input type="number" value={amountReceived} onChange={e => setAmountReceived(Number(e.target.value))} className="w-full p-3 border rounded-lg font-bold text-xl text-green-700 outline-none shadow-sm" />
                                </div>
                                <div>
                                    <label className="text-sm font-bold text-gray-700 uppercase mb-2 block">Payment Date</label>
                                    <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className="w-full p-3 border rounded-lg outline-none shadow-sm text-gray-700 font-medium cursor-pointer" />
                                </div>
                            </div>
                            <div className={`p-6 rounded-xl border-2 flex justify-between items-center ${remainingAmount <= 0 ? 'bg-green-100 border-green-400' : 'bg-red-50 border-red-200'}`}>
                                <span className="font-black uppercase tracking-wider text-gray-700">Balance Remaining:</span>
                                <span className={`text-3xl font-black ${remainingAmount <= 0 ? 'text-green-700' : 'text-red-600'}`}>{remainingAmount <= 0 ? 'FULLY PAID ✓' : `₹ ${remainingAmount.toLocaleString()}`}</span>
                            </div>
                        </>
                    )}

                    {/* --- TAB 2: UPDATE INVOICE CONTENT --- */}
                    {activeTab === 'update' && (
                        <>
                            <div className="grid grid-cols-3 gap-4 bg-blue-50/50 p-4 rounded-lg border border-blue-100 text-center">
                                <div><p className="text-xs text-gray-500 font-bold uppercase">Total Net Amount</p><p className="font-black text-gray-800">₹{(selectedData.totalAmount - selectedData.discountAmount).toLocaleString()}</p></div>
                                <div><p className="text-xs text-gray-500 font-bold uppercase">Paid So Far</p><p className="font-black text-green-600">₹{selectedData.advanceAmount.toLocaleString()}</p></div>
                                <div><p className="text-xs text-gray-500 font-bold uppercase">Current Due</p><p className="font-black text-red-600">₹{selectedData.remainingAmount.toLocaleString()}</p></div>
                            </div>
                            <div className="grid grid-cols-2 gap-6 bg-green-50 p-6 rounded-xl border border-green-200">
                                <div>
                                    <label className="text-sm font-bold text-gray-700 uppercase mb-2 block">New Payment Received</label>
                                    <input type="number" value={newPaymentAmount} onChange={e => setNewPaymentAmount(Number(e.target.value))} className="w-full p-3 border rounded-lg font-bold text-xl text-green-700 outline-none shadow-sm" />
                                </div>
                                <div>
                                    <label className="text-sm font-bold text-gray-700 uppercase mb-2 block">Payment Date</label>
                                    <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className="w-full p-3 border rounded-lg outline-none shadow-sm text-gray-700 font-medium cursor-pointer" />
                                </div>
                            </div>
                        </>
                    )}

                    {/* SUBMIT BUTTON */}
                    <button 
                        onClick={handleGenerate} disabled={isGenerating}
                        className={`w-full text-white font-bold py-4 rounded-xl shadow-lg transition-all flex justify-center items-center gap-2 text-lg uppercase tracking-wide
                            ${isGenerating ? 'bg-gray-400 cursor-not-allowed' : activeTab === 'new' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {isGenerating ? <Loader2 className="animate-spin" size={24} /> : <Save size={24} />}
                        {isGenerating ? 'Processing...' : activeTab === 'new' ? 'Save & Generate Invoice' : 'Update & Regenerate Invoice'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CreateInvoice;